import Link from 'next/link'
import dynamic from 'next/dynamic'
/* React */
import { useEffect, useRef, useState, type CSSProperties } from 'react'

/* Redux */
import Redux from './AddToCart.Redux'

/* Material */
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import useStyles from './styles'

/* Types */
import { tProduct } from 'src/shared/frontendTypes/product'
import { imageSrc } from 'src/shared/frontendTypes/images'
import { combination } from '../../functions/getCombination'

/* Components */
import AttributesSelector, { selectedAttributes } from './Components/AttributesSelector'
import QuantitySelector from './Components/QuantitySelector'
import AddToCartButton from './Components/AddToCartButton'
import StickyPurchase from './Components/StickyPurchase'
import PurchasePreview from './Components/PurchasePreview'
import presentation from '../../ProductPresentation.module.css'
import config from 'src/config'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'
import formatEuro from 'src/functions/formatEuro'
import { isFreightShippingType } from 'src/functions/shippingAvailability'
import AddToFavButton from 'src/components/AddToFavButton/AddToFavButton'
import ContactModal from 'src/components/ContactModal/DeferredContactModal'
import PriceDisplay from 'src/components/PriceDisplay'
import DiscountInfos from 'src/components/DiscountInfos'
import StockDiscountInfo from 'src/components/StockDiscountInfo'
import DeliverytimeDisplay from './Components/DeliverytimeDisplay'
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import { useProductInquiryConfig } from 'src/context/ProductInquiryContext'
import {
  pageBuilderCartAddedButtonSx,
  pageBuilderCartAddedFavoriteButtonSx,
  pageBuilderPurchaseButtonSx,
} from 'src/theme/pageBuilderButtons'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'
import { getQuantityDiscountCartPreviewText } from 'src/functions/quantityDiscountText'
import type { DisabledAttributeOptions } from '../../functions/attributeAvailability'

const PreviewModal = dynamic(() => import('../ImagesCarousel/PreviewModal'), { ssr: false })

export type Status = 'watch' | 'tried' | 'done'
type PendingOptionsAction = 'cart' | 'inquiry'

interface Props {
  product: tProduct
  combination: combination | null
  handleChangeAttribute: (attribute: number, option: number | '') => void
  selectedAttributes: selectedAttributes
  attributeNotices?: Record<number, string>
  disabledAttributeOptions?: DisabledAttributeOptions
  image?: imageSrc
  stickyPurchase?: boolean
}

const formatCouponPromotionExpirationDate = (value?: string) => {
  if (!value) return undefined

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
    year: 'numeric',
  }).format(date)
}

const AddToCart: React.FC<Props> = ({
  product: { 
    id, categorie, name, brand, brandTitel,
    attributes, path, onlyStock, shippingType, purchaseMode, inquireOnly,
    couponPromotion, images: productImages,
  },
  combination,
  selectedAttributes,
  disabledAttributeOptions = {},
  attributeNotices = {},
  handleChangeAttribute: changeAttribute,
  stickyPurchase = false,
}) => {
  const classes = useStyles()
  const discountDateActive = useDiscountDateActive(typeof combination?.price === 'object' ? combination.price.dateEnd : undefined)
  const productInquiry = useProductInquiryConfig()
  const safeAttributes = attributes || []
 // State-Deklarationen
 const [Status, setStatus] = useState<Status>('watch');
 const [Quantity, setQuantity] = useState<number>(1);
 const useOnlyStock = onlyStock || (safeAttributes.some(attribute =>
   attribute.options.some(option => option.id === selectedAttributes[attribute.id] && option.uncultivable)
 ) ? true : undefined);
 const [contactModalOpen, setContactModalOpen] = useState(false);
 const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
 const [previewImageOpen, setPreviewImageOpen] = useState(false)
 const [pendingOptionsAction, setPendingOptionsAction] = useState<PendingOptionsAction>('cart');
 const purchaseActions = useRef<HTMLDivElement>(null)
 const [dialogPalette, setDialogPalette] = useState<CSSProperties>({})

 useEffect(() => {
   if (stickyPurchase) setStatus('watch')
 }, [combination?.key, stickyPurchase])

 // Handler für Attributänderungen
 const handleChangeAttribute = (attribute: number, option: number | '') => {
   changeAttribute(attribute, option);
   if (Status === 'done') setStatus('watch');
 };

 const handleChangeQuantity = (qty: number) => {
   setQuantity(qty)
   if (stickyPurchase && Status === 'done') setStatus('watch')
 };
 const handleChangeStatus = (newStatus: Status) => setStatus(newStatus);
 const allAttributesSelected = safeAttributes.every(
   attribute => typeof selectedAttributes[attribute.id] === 'number'
 );
 const hasMissingAttributes = safeAttributes.length > 0 && !allAttributesSelected
 const resolvedPurchaseMode = purchaseMode || (inquireOnly ? 'inquiry' : 'cart');
  const isInquiryOnly = resolvedPurchaseMode === 'inquiry';
  const showInquiryButton = resolvedPurchaseMode === 'cartInquiry' || isInquiryOnly;
  const inquiryButtonLabel = isInquiryOnly
   ? productInquiry.inquiryOnlyButtonLabel
   : productInquiry.cartInquiryButtonLabel;
  const ctaButtonSx = pageBuilderPurchaseButtonSx
  const cartAdded = Status === 'done'
  const favoriteButtonSx = cartAdded
    ? pageBuilderCartAddedFavoriteButtonSx
    : ctaButtonSx
  const addToCartButtonSx = cartAdded
    ? pageBuilderCartAddedButtonSx
    : ctaButtonSx
  const showOptionsDialog = (action: PendingOptionsAction) => {
    if (stickyPurchase && purchaseActions.current) {
      const computed = getComputedStyle(purchaseActions.current)
      setDialogPalette(Object.fromEntries([
        '--page-builder-text', '--page-builder-purchase-button-bg',
        '--page-builder-purchase-button-text', '--page-builder-purchase-button-hover',
      ].map(token => [token, computed.getPropertyValue(token)]).filter(([, value]) => value.trim())) as CSSProperties)
    }
    setPendingOptionsAction(action)
    setOptionsDialogOpen(true)
  }

  // AttributesSelector ++++++++++++++++++++++++++++++++++++++++++++++++++++++++

 const renderedAttributesSelector = safeAttributes.length > 0
  ? <AttributesSelector choices={stickyPurchase}
    {...{
      attributes: safeAttributes,
      selectedAttributes,
      handleChangeAttribute,
      Status,
      disabledOptions: disabledAttributeOptions,
      notices: attributeNotices,
    }}
  />
  : null

  const showCouponPromotionBanner = Boolean(
    couponPromotion?.showOnProduct !== false
    && (couponPromotion?.title || couponPromotion?.text || couponPromotion?.badgeText)
  )
  const couponPromotionBannerClass = couponPromotion?.color === 'red'
    ? classes.couponPromotionBannerRed
    : couponPromotion?.color === 'black'
      ? classes.couponPromotionBannerBlack
      : classes.couponPromotionBannerGreen
  const couponPromotionExpirationDate = formatCouponPromotionExpirationDate(couponPromotion?.expirationDate)
  const couponPromotionExpirationLabel = couponPromotionExpirationDate
    ? `bis ${couponPromotionExpirationDate}`
    : undefined
  const couponPromotionBanner = showCouponPromotionBanner ? (
    <div className={[
      classes.couponPromotionBanner,
      couponPromotionBannerClass,
    ].join(' ')}>
      {couponPromotion?.badgeText && (
        <span className={classes.couponPromotionBadge}>
          {couponPromotion.badgeText}
        </span>
      )}
      <div className={classes.couponPromotionContent}>
        {couponPromotion?.title && (
          <strong className={classes.couponPromotionTitle}>
            {couponPromotion.title}
          </strong>
        )}
        {couponPromotion?.text && (
          <span className={classes.couponPromotionText}>
            {couponPromotion.text}
          </span>
        )}
        {couponPromotionExpirationLabel && (
          <span className={classes.couponPromotionExpires}>
            {couponPromotionExpirationLabel}
          </span>
        )}
      </div>
    </div>
  ) : null

  // In den Warenkorb ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const { getItem, addItem } = Redux()

  const handleAddToCart = () => {

    if (!combination || isInquiryOnly) {
      handleInquiryClick()
      return
    }
    const {
      key, price, priceLock, images, deliverytime, stockDeliverytime,
      stock, hash,
    } = combination
    if (useOnlyStock && (stock || 0) <= 0) {
      handleChangeStatus('tried')
      return
    }

    const cartItem = getItem(key)
    const cartQty = cartItem ? cartItem.qty : 0    

    const allesAusgewahlt = (attributes:selectedAttributes): attributes is Record<string, number> => {
      return Object.values(attributes).every(a => typeof a === 'number')
    }

    if (hasMissingAttributes){
      handleChangeStatus('tried')
      showOptionsDialog('cart')
      return
    }

    const cartAttributes = allesAusgewahlt(selectedAttributes)
      ? selectedAttributes
      : {}


    let setQty = Quantity

    if ( useOnlyStock && ((Quantity+cartQty) > (stock || 0)) ) {
      if (cartQty < (stock || 0)) setQty = (stock || 0) - cartQty
      else {
        handleChangeStatus('tried')
        return
      }
    }

    const attributesText = safeAttributes.map(attribute => ({
      titel: attribute.name,
      option:
        attribute.options.find(o => o.id === selectedAttributes[attribute.id])
          ?.name || '',
    }))

    addItem({
      id,
      categorie,
      key,
      path,
      name,
      brand: {
        id: brand,
        titel: brandTitel,
      },
      price: price,
      priceLock,
      qty: setQty,
      attributes: cartAttributes,
      attributesText,
      image: images[0],
      deliverytime,
      stockDeliverytime,
      shippingType,
      purchaseMode: resolvedPurchaseMode,
      stock, 
      onlyStock: useOnlyStock,
      hash,
    })
    setOptionsDialogOpen(false)
    handleChangeStatus('done')
  }

  const closeOptionsDialog = () => {
    setOptionsDialogOpen(false)
    setPendingOptionsAction('cart')
  }

  const handleInquiryClick = () => {
    if (hasMissingAttributes && combination) {
      handleChangeStatus('tried')
      showOptionsDialog('inquiry')
      return
    }

    setOptionsDialogOpen(false)
    setPendingOptionsAction('cart')
    setContactModalOpen(true)
  }

  const productPageUrl = productUrl(id, path)
  const productPageLink = typeof window === 'undefined'
    ? productPageUrl
    : new URL(productPageUrl, window.location.origin).toString()
  const buildContactMessage = (variant: string, link: string) => [
    'Guten Tag,',
    '',
    'ich interessiere mich für folgenden Artikel:',
    `Artikel: ${name} (#${id})`,
    `Variante: ${variant}`,
    `Link: ${link}`,
    '',
    'Bitte senden Sie mir weitere Informationen bzw. ein Angebot.',
  ].join('\n')
  const fallbackContactMessage = buildContactMessage('Anfrage / individuelle Konfiguration', productPageLink)

  const openPurchaseOptions = () => {
    showOptionsDialog(isInquiryOnly || !combination ? 'inquiry' : 'cart')
  }
  const dialogInquiry = isInquiryOnly || !combination || pendingOptionsAction === 'inquiry'
  const stickyCartItem = combination ? getItem(combination.key) : undefined
  const stickyUnavailable = Boolean(useOnlyStock && (combination?.stock || 0) <= (stickyCartItem?.qty || 0))
  const previewImages = combination?.images || productImages
  const previewSelection = safeAttributes.flatMap(attribute => {
    const selected = attribute.options.find(option => option.id === selectedAttributes[attribute.id])
    return selected ? [selected.name] : []
  }).join(' · ')
  const renderPurchasePreview = (dialog = false) => <PurchasePreview
    productId={id} name={name} images={previewImages} price={combination?.price}
    fromPrice={hasMissingAttributes && Boolean(combination)} selection={previewSelection}
    onlyStock={useOnlyStock} stock={combination?.stock} dialog={dialog}
    suspended={optionsDialogOpen || contactModalOpen || previewImageOpen}
    onOpenImage={() => setPreviewImageOpen(true)}
  />
  const imagePreview = previewImageOpen && <PreviewModal open images={previewImages} imageIndex={0}
    productID={id} metaTitle={name} fullscreen={config.productImageViewer?.variant === 'fullscreen'}
    handleClose={() => setPreviewImageOpen(false)} />
  const stickyPurchaseBar = stickyPurchase ? <StickyPurchase
    anchor={purchaseActions}
    productId={id}
    name={name}
    price={combination?.price}
    attributes={combination ? safeAttributes : []}
    selectedAttributes={selectedAttributes}
    disabledOptions={disabledAttributeOptions}
    onChangeAttribute={handleChangeAttribute}
    quantity={Quantity}
    onEdit={openPurchaseOptions}
    onAction={isInquiryOnly ? handleInquiryClick : combination ? handleAddToCart : () => setContactModalOpen(true)}
    actionLabel={!combination ? productInquiry.inquiryOnlyButtonLabel : isInquiryOnly ? inquiryButtonLabel : stickyUnavailable ? 'Nicht verfügbar' : 'In den Warenkorb'}
    missingAttributes={Boolean(combination) && hasMissingAttributes}
    unavailable={stickyUnavailable}
    inquiry={isInquiryOnly || !combination}
    added={cartAdded}
    suspended={optionsDialogOpen || contactModalOpen || previewImageOpen}
    onlyStock={useOnlyStock}
    stock={combination?.stock}
  /> : null

  const optionsDialog = safeAttributes.length > 0 || stickyPurchase
    ? <Dialog
      open={optionsDialogOpen}
      onClose={closeOptionsDialog}
      fullWidth
      maxWidth='sm'
      PaperProps={stickyPurchase ? { className: presentation.selectionDialog, style: dialogPalette, sx: { '@media (max-width: 599px)': { m: 0, mt: 'auto', width: '100%', maxHeight: '85dvh', borderRadius: '14px 14px 0 0' } } } : undefined}
    >
      <DialogTitle>{stickyPurchase ? 'Deine Auswahl' : 'Optionen auswählen'}</DialogTitle>
      {stickyPurchase && renderPurchasePreview(true)}
      <DialogContent className={classes.optionsDialogContent}>
        <Typography variant='body1' className={classes.optionsDialogText}>
          {stickyPurchase ? name : 'Es sind noch nicht alle Optionen ausgewählt. Bitte wählen Sie die fehlenden Optionen aus.'}
        </Typography>
        <AttributesSelector
          choices={stickyPurchase}
          attributes={safeAttributes}
          selectedAttributes={selectedAttributes}
          handleChangeAttribute={handleChangeAttribute}
          Status='tried'
          disabledOptions={disabledAttributeOptions}
          notices={attributeNotices}
          idPrefix={stickyPurchase ? `purchase-dialog-${id}-` : undefined}
        />
        {stickyPurchase && combination && !isInquiryOnly && <QuantitySelector
          productId={id} Quantity={Quantity} handleChangeQuantity={handleChangeQuantity}
          onlyStock={useOnlyStock} stock={combination.stock} idPrefix={`purchase-dialog-${id}-`}
        />}
        {stickyPurchase && combination && <>
          <StockDiscountInfo price={combination.price} qty={Quantity} />
        </>}
      </DialogContent>
      <DialogActions className={classes.optionsDialogActions}>
        <Button onClick={closeOptionsDialog}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='primary'
          sx={ctaButtonSx}
          disabled={(Boolean(combination) && hasMissingAttributes) || (stickyPurchase && !dialogInquiry && stickyUnavailable)}
          onClick={dialogInquiry ? handleInquiryClick : handleAddToCart}
        >
          {dialogInquiry ? (!combination ? productInquiry.inquiryOnlyButtonLabel : inquiryButtonLabel) : 'In den Warenkorb'}
        </Button>
      </DialogActions>
    </Dialog>
    : null

  // Keine Kombination +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  if (!combination) return <div className={`${classes.container} ${stickyPurchase ? presentation.buy : ''}`}>
    {renderedAttributesSelector}

    <Alert severity="info">
      Dieser Artikel ist aktuell nicht direkt online bestellbar. Kontaktieren Sie uns für Beratung, Verfügbarkeit und ein individuelles Angebot.
    </Alert>

    <div className={classes.secondaryActionRow} ref={purchaseActions}>
      <Button
        variant='contained'
        color='primary'
        size='large'
        fullWidth
        className={classes.inquiryButton}
        sx={ctaButtonSx}
        onClick={() => setContactModalOpen(true)}
      >
        {productInquiry.inquiryOnlyButtonLabel}
      </Button>
    </div>

    <ContactModal
      open={contactModalOpen}
      initialSubject='angebot'
      initialMessage={fallbackContactMessage}
      onClose={() => setContactModalOpen(false)}
    />
    {optionsDialog}
    {imagePreview}
    {stickyPurchaseBar}
  </div>

  // Render ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const { key, price, deliverytime, stockDeliverytime, stock } = combination
  const onlyStockUnavailable = Boolean(useOnlyStock && (stock || 0) <= 0)
  const cartItem = getItem(key)
  const cartQuantity = Math.max(0, Math.floor(cartItem?.qty || 0))
  const quantityDiscountPreviewQty = Quantity + cartQuantity
  const showQuantityDiscount = (!stickyPurchase || discountDateActive) && typeof price !== 'number'
    && price.showPercent !== false
    && isQuantityDiscountAvailable(price.quantityDiscount, {
      onlyStock: useOnlyStock,
      stock,
    })
  const quantityDiscountText = typeof price !== 'number' && showQuantityDiscount
    ? getQuantityDiscountCartPreviewText(price.quantityDiscount, cartQuantity, Quantity)
    : undefined
  const purchaseOffer = stickyPurchase && typeof price !== 'number' ? <DiscountInfos
    discount={price}
    qty={quantityDiscountPreviewQty}
    component='div'
    align='left'
    size='small'
    className={presentation.offer}
    clock={{ appearance: 'purchase', component: 'div' }}
    showQuantityDiscount={showQuantityDiscount}
    quantityDiscountText={quantityDiscountText}
  /> : null
  const articleUrl = productVariantUrl(id, path, combination.key)
  const articleLink = typeof window === 'undefined'
    ? articleUrl
    : new URL(articleUrl, window.location.origin).toString()
  const combinationAttributes = combination.attributes || []
  const articleVariant = combinationAttributes.length > 0
    ? combinationAttributes.join(', ')
    : 'Standard'
  const contactMessage = buildContactMessage(articleVariant, articleLink)

  if (isInquiryOnly) return <div className={`${classes.container} ${stickyPurchase ? presentation.buy : ''}`}>
    {stickyPurchase && renderPurchasePreview()}
    {purchaseOffer}
    {renderedAttributesSelector}

    {!stickyPurchase && typeof price !== 'number' && (
      <DiscountInfos
        discount={price}
        qty={quantityDiscountPreviewQty}
        component='div'
        variant='h5'
        align='center'
        className={classes.discountInfos}
        clock={{ variant: 'h5' }}
        size='medium'
        showQuantityDiscount={showQuantityDiscount}
        quantityDiscountText={quantityDiscountText}
      />
    )}

{!stickyPurchase && <>
    <PriceDisplay
      {...{ price }}
      component='div'
      variant='h4'
      align='left'
      layout={stickyPurchase ? 'purchase' : 'detail'}
      showTaxHint
      className={classes.priceDisplay}
      onlyStock={useOnlyStock}
      stock={stock}
    />
    </>}

    <StockDiscountInfo
      price={price}
      qty={Quantity}
      className={classes.stockDiscountInfo}
    />

    {couponPromotionBanner}

    <DeliverytimeDisplay
      compact={stickyPurchase}
      {...{ deliverytime, onlyStock: useOnlyStock }}
      stockDeliverytime={stockDeliverytime}
      inquireOnly
      inquiredQty={Quantity}
      stockQty={stock}
    />

    <Alert severity='info'>
      {productInquiry.onlyInquiryNotice}
    </Alert>

    {safeAttributes.length > 0 && !allAttributesSelected && (
      <Alert severity='warning'>
        {productInquiry.selectionHint}
      </Alert>
    )}

    <div className={`${classes.buttonsWrapper} ${stickyPurchase ? presentation.actions : ''}`} ref={purchaseActions}>
      <AddToFavButton
        pId={id}
        pKey={key}
        className={classes.addToFavButton}
        sx={ctaButtonSx}
        color='primary' size='large' variant='contained' fullWidth
      />
      <Button
        variant='contained'
        color='primary'
        size='large'
        fullWidth
        className={classes.inquiryButton}
        sx={ctaButtonSx}
        onClick={handleInquiryClick}
      >
        {inquiryButtonLabel}
      </Button>
    </div>

    <ContactModal
      open={contactModalOpen}
      initialSubject='angebot'
      initialMessage={contactMessage}
      onClose={() => setContactModalOpen(false)}
    />

    {optionsDialog}
    {imagePreview}
    {stickyPurchaseBar}
  </div>

  return <div className={`${classes.container} ${stickyPurchase ? presentation.buy : ''}`}>
    {stickyPurchase && renderPurchasePreview()}
    {purchaseOffer}
    {renderedAttributesSelector}

    {!onlyStockUnavailable &&
      <QuantitySelector
        productId={id}
        {...{ Quantity, handleChangeQuantity, onlyStock:useOnlyStock, stock }}
      />
    }

    {!stickyPurchase && typeof price !== 'number' && (
      <DiscountInfos
        discount={price}
        qty={quantityDiscountPreviewQty}
        component='div'
        variant='h5'
        align='center'
        className={classes.discountInfos}
        clock={{ variant: 'h5' }}
        size='medium'
        showQuantityDiscount={showQuantityDiscount}
        quantityDiscountText={quantityDiscountText}
      />
    )}

{!stickyPurchase && <>
    <PriceDisplay
      {...{ price }}
      component='div'
      variant='h4'
      align='left'
      layout={stickyPurchase ? 'purchase' : 'detail'}
      showTaxHint
      className={classes.priceDisplay}
      onlyStock={useOnlyStock}
      stock={stock}
    />
    </>}

    <StockDiscountInfo
      price={price}
      qty={Quantity}
      className={classes.stockDiscountInfo}
    />

    {couponPromotionBanner}
    
    <DeliverytimeDisplay
      compact={stickyPurchase}
      {...{ deliverytime, onlyStock:useOnlyStock }}
      stockDeliverytime={stockDeliverytime}
      inquireOnly={isInquiryOnly}
      inquiredQty={Quantity}
      stockQty={stock}
    />

    <div className={`${classes.buttonsWrapper} ${stickyPurchase ? presentation.actions : ''}`} ref={purchaseActions}>
      <AddToFavButton 
        pId={id}
        pKey={key}
        className={classes.addToFavButton}
        sx={favoriteButtonSx}
        color='primary' size='large' variant='contained' fullWidth
      />
      <AddToCartButton
        {...{ handleAddToCart, Status }}
        disabled={onlyStockUnavailable}
        label={onlyStockUnavailable ? 'Nicht verfügbar' : quantityDiscountText?.actionLabel}
        sx={addToCartButtonSx}
      />
    </div>

    {showInquiryButton && !onlyStockUnavailable && (
      <div className={classes.secondaryActionRow}>
        <Button
          variant='contained'
          color='primary'
          size='large'
          fullWidth
          className={classes.inquiryButton}
          sx={ctaButtonSx}
          onClick={handleInquiryClick}
        >
          {inquiryButtonLabel}
        </Button>
      </div>
    )}

    {stickyPurchase && <p className={presentation.shippingNote}>
      {!isFreightShippingType(shippingType) && config.mindermengenzuschlag.betrag > 0 && <>Unter {formatEuro(config.mindermengenzuschlag.entfaelltAb)} Warenwert: {formatEuro(config.mindermengenzuschlag.betrag)} Mindermengenzuschlag. </>}
      <Link href='/zahlung-und-versand'>Versand & Lieferung</Link>
    </p>}
    {cartItem &&
      <div className={classes.InCartInfo}>
        Artikel {cartItem.qty} x im Warenkorb
      </div>
    }

    {showInquiryButton && !onlyStockUnavailable && (
      <ContactModal
        open={contactModalOpen}
        initialSubject='angebot'
        initialMessage={contactMessage}
        onClose={() => setContactModalOpen(false)}
      />
    )}

    {optionsDialog}
    {imagePreview}
    {stickyPurchaseBar}

  </div>

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
}

export default AddToCart
